import DataLoader from 'dataloader'
import { type JobsApi } from '@/features/jobs/api/job-api'
import {
  BatchGetJobsRequest,
  type Job as JobProto,
} from '@taxfyle/api-internal/internal/job_pb'
import { map, Subject } from 'rxjs'
import { filterNonNil } from '@/utils/rx-util'

/**
 * Data loader for caching job lookups.
 * Most job representations use the same backend endpoint,
 * so we can cache the returned resources and also take
 * advantage of batching.
 */
export class JobDataLoader {
  private readonly _loader = new DataLoader(this._fetchManyCore.bind(this))

  private readonly _clearSubject = new Subject<string>()

  /**
   * Emits when we're clearing a job from the cache.
   */
  readonly clear$ = this._clearSubject.asObservable()

  constructor(private readonly _api: JobsApi) {
    this.fetch = this.fetch.bind(this)
    this.fetchMany = this.fetchMany.bind(this)

    // Keep the underlying cache updated in response to real-time events.
    this._api.realtime$
      .pipe(
        map((evt) => evt.getJobUpdated()?.getJob()),
        filterNonNil(),
      )
      .subscribe((updatedProto) => {
        this._loader.clear(updatedProto.getId())
        this._loader.prime(updatedProto.getId(), updatedProto)
      })
  }

  /**
   * Clears a cached entry so the next load attempt will call the remote.
   *
   * @param id
   */
  clear(id: string) {
    this._loader.clear(id)
    this._clearSubject.next(id)
  }

  /**
   * Fetches a job by ID.
   *
   * @param id
   */
  async fetch(id: string): Promise<JobProto | null> {
    return this._loader.load(id)
  }

  /**
   * Fetches multiple jobs by their ID.
   *
   * @param ids
   */
  async fetchMany(ids: readonly string[]): Promise<(JobProto | null)[]> {
    return Promise.all(ids.map((id) => this._loader.load(id)))
  }

  /**
   * Fetches the jobs by IDs.
   *
   * @param ids
   * @private
   */
  private async _fetchManyCore(
    ids: readonly string[],
  ): Promise<(JobProto | null)[]> {
    const request = new BatchGetJobsRequest().setIdsList([...ids])

    const response = await this._api.jobs.batchGetJobs(request)
    return response.getJobsList().map((resource) => {
      const view = resource.getCollaboratorView() ?? resource.getAdminView()
      if (!view) {
        return null
      }

      // Filter out archived jobs
      if (view.getArchived()) {
        return null
      }

      return view
    })
  }
}
